import { useEffect, useRef, useState } from 'react'
import {
  ChevronDown, ChevronUp, Phone, MapPin,
  CalendarDays, StickyNote, RefreshCw, PenLine,
  ShieldCheck, Clock, FileText, Upload, Send,
  CheckCircle2, AlertTriangle, Loader2, ExternalLink,
  BarChart2, Package,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import AdminLayout from '@/components/admin/AdminLayout'
import Button from '@/components/ui/Button'

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTS = [
  { val: '',                     label: 'All'             },
  { val: 'pending',              label: 'Pending'         },
  { val: 'site_visit_required',  label: 'Site Visit Req.' },
  { val: 'site_visit_scheduled', label: 'Visit Scheduled' },
  { val: 'in_progress',          label: 'In Progress'     },
  { val: 'uploaded',             label: 'Uploaded'        },
]

const STATUS_LABEL = {
  pending:               { text: 'Pending',         color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25'          },
  site_visit_required:   { text: 'Site Visit Req.', color: 'text-orange-400 bg-orange-500/10 border-orange-500/25'          },
  site_visit_scheduled:  { text: 'Visit Scheduled', color: 'text-blue-400 bg-blue-500/10 border-blue-500/25'                },
  in_progress:           { text: 'In Progress',     color: 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25'      },
  uploaded:              { text: 'Uploaded',        color: 'text-green-400 bg-green-500/10 border-green-500/25'             },
}

const PROPERTY_LABEL = {
  residential_house: 'Residential House',
  condo_unit:        'Condo Unit',
  commercial_office: 'Commercial Office',
  industrial:        'Industrial',
}

const CONTRACT_STATUS_UI = {
  pending_customer: { text: 'Awaiting Customer Signature', color: 'text-yellow-400', icon: Clock       },
  pending_sk:       { text: 'Awaiting SK Countersign',     color: 'text-orange-400', icon: PenLine     },
  fully_signed:     { text: 'Fully Signed',                color: 'text-green-400',  icon: ShieldCheck },
  voided:           { text: 'Voided',                      color: 'text-siglat-red', icon: null        },
}

const NEXT_STATUS = {
  pending:              'site_visit_required',
  site_visit_required:  'site_visit_scheduled',
  site_visit_scheduled: 'in_progress',
  in_progress:          'uploaded',
}

const NEXT_STATUS_LABEL = {
  pending:              'Mark: Site Visit Required',
  site_visit_required:  'Mark: Visit Scheduled',
  site_visit_scheduled: 'Mark: In Progress',
  in_progress:          'Mark: Uploaded',
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const peso = (n) =>
  n != null && !isNaN(n)
    ? '₱' + Number(n).toLocaleString('en-PH', { minimumFractionDigits: 2 })
    : '—'

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] ?? { text: status, color: 'text-ink-muted bg-surface-overlay border-surface-border' }
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full border font-medium ${s.color}`}>
      {s.text}
    </span>
  )
}

// ── System Estimate (BOQ) Viewer ──────────────────────────────────────────────

function EstimateViewer({ quoteId }) {
  const [quote,   setQuote]   = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!quoteId) { setLoading(false); return }
    supabase
      .from('quotes')
      .select(`
        id, quote_reference, scope_summary, labor_amount,
        total_min, total_max, est_duration, ai_confidence, flags,
        scope_items, materials_items, indirect_cost_breakdown,
        markup_rate, preliminaries, created_at
      `)
      .eq('id', quoteId)
      .single()
      .then(({ data }) => { setQuote(data); setLoading(false) })
  }, [quoteId])

  if (loading) return (
    <div className="flex items-center gap-2 py-2">
      <Loader2 className="w-4 h-4 animate-spin text-siglat-teal" />
      <p className="text-xs text-ink-muted">Loading System Estimate…</p>
    </div>
  )

  if (!quote) return (
    <p className="text-xs text-ink-muted italic">No System Estimate found for this request.</p>
  )

  const sections = Array.isArray(quote.scope_items) ? quote.scope_items : []
  const materials = Array.isArray(quote.materials_items) ? quote.materials_items : []
  const indirect = quote.indirect_cost_breakdown ?? {}
  const flags = Array.isArray(quote.flags) ? quote.flags : []

  return (
    <div className="flex flex-col gap-4">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-mono text-siglat-teal">{quote.quote_reference}</p>
          <p className="text-xs text-ink-muted mt-0.5">
            {quote.est_duration && `Duration: ${quote.est_duration} · `}
            AI Confidence: <span className={`font-medium ${quote.ai_confidence === 'high' ? 'text-green-400' : quote.ai_confidence === 'medium' ? 'text-yellow-400' : 'text-siglat-red'}`}>{quote.ai_confidence}</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-ink-muted">Client Range</p>
          <p className="text-sm font-bold text-ink-primary">{peso(quote.total_min)} – {peso(quote.total_max)}</p>
        </div>
      </div>

      {/* Scope summary */}
      {quote.scope_summary && (
        <div className="bg-surface-overlay rounded-lg p-3">
          <p className="text-[10px] text-ink-muted uppercase tracking-widest mb-1">Scope Summary</p>
          <p className="text-sm text-ink-primary leading-relaxed">{quote.scope_summary}</p>
        </div>
      )}

      {/* Flags */}
      {flags.length > 0 && (
        <div className="bg-yellow-500/8 border border-yellow-500/20 rounded-lg p-3 flex flex-col gap-1">
          <p className="text-[10px] text-yellow-400 uppercase tracking-widest font-semibold">AI Flags</p>
          {flags.map((f, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <AlertTriangle className="w-3 h-3 text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-300">{f}</p>
            </div>
          ))}
        </div>
      )}

      {/* BOQ Sections */}
      {sections.length > 0 && (
        <div>
          <p className="text-[10px] text-ink-muted uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <BarChart2 className="w-3 h-3" /> Bill of Quantities
          </p>
          <div className="border border-surface-border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-surface-overlay border-b border-surface-border">
                  <th className="text-left px-3 py-2 text-ink-muted font-medium">Item</th>
                  <th className="text-left px-2 py-2 text-ink-muted font-medium hidden sm:table-cell">Unit</th>
                  <th className="text-right px-2 py-2 text-ink-muted font-medium">Qty</th>
                  <th className="text-right px-2 py-2 text-ink-muted font-medium hidden md:table-cell">Direct Cost</th>
                  <th className="text-right px-3 py-2 text-ink-muted font-medium">Client Total</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section, si) => (
                  <>
                    <tr key={`sec-${si}`} className="bg-surface-overlay/50">
                      <td colSpan={5} className="px-3 py-1.5 text-[10px] font-semibold text-ink-secondary uppercase tracking-wider">
                        {section.section_number}. {section.section_name}
                      </td>
                    </tr>
                    {(section.items || []).map((item, ii) => (
                      <tr key={`item-${si}-${ii}`} className="border-t border-surface-border/50 hover:bg-surface-overlay/30">
                        <td className="px-3 py-2 text-ink-primary leading-snug">
                          <span className="text-ink-muted mr-1">{item.item_number}</span>
                          {item.description}
                        </td>
                        <td className="px-2 py-2 text-ink-muted hidden sm:table-cell">{item.unit}</td>
                        <td className="px-2 py-2 text-right text-ink-secondary">{item.quantity}</td>
                        <td className="px-2 py-2 text-right text-ink-muted hidden md:table-cell">{peso(item.direct_line_total)}</td>
                        <td className="px-3 py-2 text-right font-medium text-ink-primary">{peso(item.client_line_total)}</td>
                      </tr>
                    ))}
                    <tr key={`sec-total-${si}`} className="border-t border-surface-border bg-surface-overlay">
                      <td colSpan={4} className="px-3 py-1.5 text-xs text-ink-secondary font-medium">Section {section.section_number} Total</td>
                      <td className="px-3 py-1.5 text-right text-xs font-bold text-ink-primary">{peso(section.section_client_total)}</td>
                    </tr>
                  </>
                ))}
                <tr className="border-t-2 border-siglat-teal/30 bg-siglat-teal/5">
                  <td colSpan={4} className="px-3 py-2 text-sm font-bold text-ink-primary">Grand Total (Client Price)</td>
                  <td className="px-3 py-2 text-right text-sm font-bold text-siglat-teal">{peso(quote.total_max)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Indirect Cost Breakdown */}
      {Object.keys(indirect).length > 0 && (
        <div>
          <p className="text-[10px] text-ink-muted uppercase tracking-widest mb-2">Indirect Cost Breakdown ({Math.round((quote.markup_rate ?? 0.391) * 100)}% markup)</p>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(indirect)
              .filter(([k]) => k !== 'total_indirect')
              .map(([key, val]) => (
                <div key={key} className="flex justify-between text-xs px-2 py-1 bg-surface-overlay rounded">
                  <span className="text-ink-muted capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-ink-secondary font-medium">{peso(val)}</span>
                </div>
              ))
            }
            <div className="col-span-2 flex justify-between text-xs px-2 py-1 bg-surface-overlay rounded border-t border-surface-border font-semibold">
              <span className="text-ink-primary">Total Indirect</span>
              <span className="text-ink-primary">{peso(indirect.total_indirect)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Materials Takeoff */}
      {materials.length > 0 && (
        <div>
          <p className="text-[10px] text-ink-muted uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <Package className="w-3 h-3" /> Materials Take-Off
          </p>
          <div className="border border-surface-border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-surface-overlay border-b border-surface-border">
                  <th className="text-left px-3 py-2 text-ink-muted font-medium">Material</th>
                  <th className="text-left px-2 py-2 text-ink-muted font-medium hidden sm:table-cell">Specification</th>
                  <th className="text-right px-3 py-2 text-ink-muted font-medium">Qty</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((m, i) => (
                  <tr key={i} className="border-t border-surface-border/50">
                    <td className="px-3 py-2 text-ink-primary">{m.item}</td>
                    <td className="px-2 py-2 text-ink-muted hidden sm:table-cell">{m.specification ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-ink-secondary">{m.quantity} {m.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  )
}

// ── Final Proposal Upload ─────────────────────────────────────────────────────

function FinalProposalUpload({ row, onSent }) {
  const adminProfile = useAuthStore((s) => s.profile)
  const fileRef      = useRef(null)

  const [existing,      setExisting]      = useState(null)
  const [loadingExist,  setLoadingExist]  = useState(true)
  const [paymentType,   setPaymentType]   = useState('full')
  const [pdfSignedUrl,  setPdfSignedUrl]  = useState(null)
  const [downpctInput,  setDownpctInput]  = useState('50')
  const [totalInput,    setTotalInput]    = useState('')
  const [adminNotes,    setAdminNotes]    = useState('')
  const [file,          setFile]          = useState(null)
  const [uploading,     setUploading]     = useState(false)
  const [sending,       setSending]       = useState(false)
  const [err,           setErr]           = useState(null)

  useEffect(() => {
    if (!row.quote_id) { setLoadingExist(false); return }
    supabase
      .from('final_proposals')
      .select('id, status, pdf_path, payment_type, downpayment_pct, total_amount, admin_notes, sent_at')
      .eq('quote_id', row.quote_id)
      .maybeSingle()
      .then(({ data }) => { setExisting(data); setLoadingExist(false) })
  }, [row.quote_id])

  useEffect(() => {
    if (!existing?.pdf_path) { setPdfSignedUrl(null); return }
    supabase.storage.from('final-proposals')
      .createSignedUrl(existing.pdf_path, 3600)
      .then(({ data }) => setPdfSignedUrl(data?.signedUrl ?? null))
  }, [existing?.pdf_path])

  function handleFileChange(e) {
    const f = e.target.files?.[0]
    if (f) setFile(f)
  }

  async function handleSend() {
    if (!totalInput || isNaN(Number(totalInput))) {
      setErr('Enter a valid total amount.')
      return
    }
    // PDF is required — must either upload new file or have an existing one
    if (!file && !existing?.pdf_path) {
      setErr('Please upload the final proposal PDF before sending.')
      return
    }
    setErr(null)
    setUploading(true)

    let pdfPath = existing?.pdf_path ?? null

    // Upload PDF if a new file was selected
    if (file) {
      const ext       = file.name.split('.').pop() || 'pdf'
      const storagePath = `${row.customer_id}/${row.id}/${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage
        .from('final-proposals')
        .upload(storagePath, file, { upsert: true })
      if (upErr) { setErr('PDF upload failed: ' + upErr.message); setUploading(false); return }
      pdfPath = storagePath
    }

    setUploading(false)
    setSending(true)

    const payload = {
      quote_id:         row.quote_id,
      booking_id:       row.booking_id,
      payment_type:     paymentType,
      downpayment_pct:  paymentType === 'downpayment' ? parseInt(downpctInput) : null,
      total_amount:     Number(totalInput),
      admin_notes:      adminNotes || null,
      pdf_path:         pdfPath,
      status:           'sent',
      sent_at:          new Date().toISOString(),
      uploaded_by:      adminProfile?.id,
    }

    let fpError
    if (existing?.id) {
      const { error } = await supabase
        .from('final_proposals')
        .update(payload)
        .eq('id', existing.id)
      fpError = error
    } else {
      const { error } = await supabase
        .from('final_proposals')
        .insert(payload)
      fpError = error
    }

    if (fpError) { setErr('Failed to save: ' + fpError.message); setSending(false); return }

    // Advance fpr status to 'uploaded'
    await supabase
      .from('final_proposal_requests')
      .update({ status: 'uploaded' })
      .eq('id', row.id)

    supabase.functions.invoke('send-notification', { body: {
      type: 'proposal_ready', customer_id: row.customer_id,
      data: { ref_number: row._quoteRef ?? '', amount: totalInput ? `PHP ${Number(totalInput).toLocaleString()}` : null },
    }}).catch(console.error)
    setSending(false)
    onSent()
  }

  if (loadingExist) return <p className="text-xs text-ink-muted">Loading…</p>

  // Already sent — show status
  if (existing?.status === 'sent' || existing?.status === 'accepted') {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-400" />
          <p className="text-sm font-medium text-green-400">
            Final Proposal {existing.status === 'accepted' ? 'Accepted by Client' : 'Sent to Client'}
          </p>
        </div>
        {existing.sent_at && (
          <p className="text-xs text-ink-muted">
            Sent: {new Date(existing.sent_at).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        )}
        {existing.total_amount && (
          <p className="text-xs text-ink-secondary">
            Total: <span className="font-semibold text-ink-primary">{peso(existing.total_amount)}</span>
            {existing.payment_type === 'downpayment' && ` (${existing.downpayment_pct}% downpayment)`}
          </p>
        )}
        {existing.pdf_path && pdfSignedUrl && (
          <a
            href={pdfSignedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-siglat-teal hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            View uploaded PDF
          </a>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">

      {/* Total amount */}
      <div>
        <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">
          Final Total Amount (₱) <span className="text-siglat-red">*</span>
        </label>
        <input
          type="number"
          value={totalInput}
          onChange={(e) => setTotalInput(e.target.value)}
          placeholder="e.g. 85000"
          className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-siglat-teal"
        />
      </div>

      {/* Payment terms */}
      <div>
        <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-2">Payment Terms</label>
        <div className="flex gap-2">
          {[{ val: 'full', label: 'Full Payment' }, { val: 'downpayment', label: 'Downpayment' }].map((opt) => (
            <button
              key={opt.val}
              type="button"
              onClick={() => setPaymentType(opt.val)}
              className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors
                ${paymentType === opt.val
                  ? 'bg-siglat-teal/15 border-siglat-teal text-siglat-teal'
                  : 'bg-surface-overlay border-surface-border text-ink-muted hover:border-siglat-teal/40'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {paymentType === 'downpayment' && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="number"
              min="10" max="90"
              value={downpctInput}
              onChange={(e) => setDownpctInput(e.target.value)}
              className="w-20 bg-surface-overlay border border-surface-border rounded-lg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal"
            />
            <span className="text-sm text-ink-secondary">% downpayment required</span>
          </div>
        )}
      </div>

      {/* PDF upload */}
      <div>
        <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">
          Attach Final Proposal PDF <span className="text-ink-muted normal-case font-normal">(optional)</span>
        </label>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-surface-border text-sm text-ink-secondary hover:border-siglat-teal hover:text-siglat-teal transition-colors w-full justify-center"
        >
          <Upload className="w-4 h-4" />
          {file ? file.name : 'Choose PDF file'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,application/pdf"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Admin notes */}
      <div>
        <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1.5">
          Notes to Client <span className="text-ink-muted normal-case font-normal">(optional)</span>
        </label>
        <textarea
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          rows={2}
          placeholder="Any remarks or instructions for the client…"
          className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted resize-none focus:outline-none focus:ring-1 focus:ring-siglat-teal"
        />
      </div>

      {err && (
        <div className="flex items-center gap-2 p-2.5 bg-siglat-red/10 border border-siglat-red/25 rounded-lg">
          <AlertTriangle className="w-3.5 h-3.5 text-siglat-red shrink-0" />
          <p className="text-xs text-siglat-red">{err}</p>
        </div>
      )}

      <Button
        onClick={handleSend}
        disabled={uploading || sending || !totalInput}
        className="w-full"
      >
        {uploading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading PDF…</>
          : sending
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
            : <><Send className="w-4 h-4" /> Send Final Proposal to Client</>
        }
      </Button>

    </div>
  )
}

// ── Proposal Row ──────────────────────────────────────────────────────────────

function ProposalRow({ row, onStatusChange }) {
  const adminProfile = useAuthStore((s) => s.profile)

  const [expanded,     setExpanded]     = useState(false)
  const [updating,     setUpdating]     = useState(false)
  const [contract,     setContract]     = useState(null)
  const [contrLoading, setContrLoading] = useState(false)
  const [signing,      setSigning]      = useState(false)
  const [activeTab,    setActiveTab]    = useState('details') // 'details' | 'estimate' | 'proposal'

  async function loadContract() {
    if (contract !== null || contrLoading) return
    if (!row.quote_id) return
    setContrLoading(true)
    const { data: fp } = await supabase
      .from('final_proposals')
      .select('id')
      .eq('quote_id', row.quote_id)
      .maybeSingle()
    if (fp?.id) {
      const { data: c } = await supabase
        .from('contracts')
        .select('id, status, customer_name_signed, customer_signed_at, customer_ip_address')
        .eq('proposal_id', fp.id)
        .maybeSingle()
      setContract(c ?? null)
    } else {
      setContract(null)
    }
    setContrLoading(false)
  }

  function handleExpand() {
    setExpanded((v) => {
      if (!v) loadContract()
      return !v
    })
  }

  async function handleCountersign() {
    if (!contract?.id || !adminProfile?.id) return
    setSigning(true)
    const now = new Date().toISOString()
    const { error } = await supabase
      .from('contracts')
      .update({ sk_signed_by: adminProfile.id, sk_signed_at: now, status: 'fully_signed' })
      .eq('id', contract.id)
    if (!error) {
      supabase.functions.invoke('generate-contract-pdf', {
        body: { contract_id: contract.id },
      }).catch(console.error)
      supabase.functions.invoke('send-notification', { body: {
        type: 'contract_signed', customer_id: contract.customer_id,
        data: { ref_number: contract.quote_reference ?? row._quoteRef ?? '' },
      }}).catch(console.error)
      setContract((c) => ({ ...c, status: 'fully_signed', sk_signed_at: now }))
    }
    setSigning(false)
  }

  async function advanceStatus() {
    const next = NEXT_STATUS[row.status]
    if (!next) return
    setUpdating(true)
    await supabase.from('final_proposal_requests').update({ status: next }).eq('id', row.id)
    onStatusChange(row.id, next)
    setUpdating(false)
  }

  const catName  = row._category  ?? 'Service'
  const address  = row._address   ?? ''
  const city     = address.split(',').slice(-2, -1)[0]?.trim() ?? address.split(',').pop()?.trim() ?? ''
  const dateStr  = new Date(row.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
  const visitStr = row.preferred_visit_date
    ? new Date(row.preferred_visit_date + 'T00:00:00').toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
    : null

  const TABS = [
    { key: 'details',  label: 'Details'         },
    { key: 'estimate', label: 'System Estimate'  },
    { key: 'proposal', label: 'Send Proposal'    },
  ]

  return (
    <div className="bg-surface-raised border border-surface-border rounded-xl overflow-hidden">

      {/* Summary row */}
      <button
        type="button"
        onClick={handleExpand}
        className="w-full px-4 py-3.5 flex items-center justify-between gap-3 text-left hover:bg-surface-overlay transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="text-sm font-semibold text-ink-primary">{row._fullName ?? 'Customer'}</p>
            <StatusBadge status={row.status} />
          </div>
          <p className="text-xs text-siglat-teal font-mono">{row._quoteRef ?? '—'}</p>
          <p className="text-xs text-ink-muted mt-0.5">
            {catName}{city ? ` — ${city}` : ''} · {dateStr}
          </p>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-ink-muted shrink-0" />
          : <ChevronDown className="w-4 h-4 text-ink-muted shrink-0" />
        }
      </button>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-surface-border">

          {/* Tab bar */}
          <div className="flex border-b border-surface-border bg-surface-overlay/40">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 text-xs py-2.5 font-medium transition-colors border-b-2 -mb-px
                  ${activeTab === tab.key
                    ? 'border-siglat-teal text-siglat-teal'
                    : 'border-transparent text-ink-muted hover:text-ink-primary'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="px-4 py-4 flex flex-col gap-4">

            {/* ── Tab: Details ─────────────────────────────── */}
            {activeTab === 'details' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <InfoRow icon={<Phone className="w-3.5 h-3.5" />}       label="Email"            value={row._email ?? '—'} />
                  <InfoRow icon={<Phone className="w-3.5 h-3.5" />}       label="Viber"            value={row.viber_number ?? 'Not provided'} />
                  <InfoRow icon={<MapPin className="w-3.5 h-3.5" />}      label="Property Type"    value={PROPERTY_LABEL[row.property_type] ?? row.property_type ?? '—'} />
                  {visitStr && (
                    <InfoRow icon={<CalendarDays className="w-3.5 h-3.5" />} label="Preferred Visit" value={visitStr} />
                  )}
                </div>
                {address && <InfoRow icon={<MapPin className="w-3.5 h-3.5" />} label="Full Address" value={address} />}
                {row.additional_notes && (
                  <div className="bg-surface-overlay border border-surface-border rounded-lg p-3">
                    <div className="flex items-center gap-1.5 mb-1 text-ink-muted">
                      <StickyNote className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Additional Notes</span>
                    </div>
                    <p className="text-sm text-ink-primary">{row.additional_notes}</p>
                  </div>
                )}

                {/* Status advance */}
                {NEXT_STATUS[row.status] && (
                  <Button size="sm" variant="secondary" loading={updating} onClick={advanceStatus}>
                    <RefreshCw className="w-3.5 h-3.5" />
                    {NEXT_STATUS_LABEL[row.status]}
                  </Button>
                )}
                {row.status === 'uploaded' && (
                  <p className="text-xs text-green-400 font-medium">Final proposal sent to client.</p>
                )}

                {/* Contract section */}
                {contrLoading && <p className="text-xs text-ink-muted">Loading contract…</p>}
                {!contrLoading && contract && (() => {
                  const ui   = CONTRACT_STATUS_UI[contract.status] ?? CONTRACT_STATUS_UI.pending_customer
                  const Icon = ui.icon
                  return (
                    <div className="border border-surface-border rounded-xl overflow-hidden">
                      <div className="px-4 py-3 bg-surface-overlay flex items-center gap-2">
                        {Icon && <Icon className={`w-3.5 h-3.5 ${ui.color}`} />}
                        <p className={`text-xs font-semibold ${ui.color}`}>{ui.text}</p>
                      </div>
                      <div className="px-4 py-3 flex flex-col gap-2">
                        {contract.customer_name_signed && (
                          <p className="text-xs text-ink-muted">
                            Signed by: <span className="text-ink-primary font-medium">{contract.customer_name_signed}</span>
                            {contract.customer_signed_at && (
                              <span className="ml-1">· {new Date(contract.customer_signed_at).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                            )}
                          </p>
                        )}
                        {contract.customer_ip_address && (
                          <p className="text-xs text-ink-muted">IP: {contract.customer_ip_address}</p>
                        )}
                        {contract.status === 'pending_sk' && (
                          <Button size="sm" loading={signing} onClick={handleCountersign}>
                            <PenLine className="w-3.5 h-3.5" />
                            Sign as Siglat Konstract
                          </Button>
                        )}
                        {contract.status === 'fully_signed' && (
                          <div className="flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
                            <p className="text-xs text-green-400 font-medium">Contract fully executed</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })()}
              </>
            )}

            {/* ── Tab: System Estimate ─────────────────────── */}
            {activeTab === 'estimate' && (
              <EstimateViewer quoteId={row.quote_id} />
            )}

            {/* ── Tab: Send Proposal ───────────────────────── */}
            {activeTab === 'proposal' && (
              <FinalProposalUpload
                row={row}
                onSent={() => onStatusChange(row.id, 'uploaded')}
              />
            )}

          </div>
        </div>
      )}
    </div>
  )
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-ink-muted mt-0.5 shrink-0">{icon}</span>
      <div>
        <p className="text-[10px] text-ink-muted uppercase tracking-widest">{label}</p>
        <p className="text-sm text-ink-primary break-words">{value}</p>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminProposals() {
  const [rows,       setRows]       = useState([])
  const [loading,    setLoading]    = useState(true)
  const [filter,     setFilter]     = useState('')
  const [refreshing, setRefreshing] = useState(false)

  async function fetchRows() {
    setLoading(true)

    // Step 1: fetch base fpr records — only where customer explicitly submitted
    // (quote_id must exist — no orphaned/incomplete FPRs)
    let q = supabase
      .from('final_proposal_requests')
      .select('id, status, created_at, viber_number, preferred_visit_date, property_type, additional_notes, customer_id, quote_id, booking_id')
      .not('quote_id', 'is', null)
      .order('created_at', { ascending: false })

    if (filter) q = q.eq('status', filter)

    const { data: fprs, error } = await q
    if (error || !fprs?.length) {
      setRows([])
      setLoading(false)
      return
    }

    // Step 2: batch fetch profiles
    const customerIds = [...new Set(fprs.map((r) => r.customer_id).filter(Boolean))]
    const quoteIds    = [...new Set(fprs.map((r) => r.quote_id).filter(Boolean))]
    const bookingIds  = [...new Set(fprs.map((r) => r.booking_id).filter(Boolean))]

    const [profileRes, quoteRes, bookingRes] = await Promise.all([
      customerIds.length
        ? supabase.from('profiles').select('id, full_name, email').in('id', customerIds)
        : { data: [] },
      quoteIds.length
        ? supabase.from('quotes').select('id, quote_reference').in('id', quoteIds)
        : { data: [] },
      bookingIds.length
        ? supabase.from('bookings').select('id, address, service_categories(name)').in('id', bookingIds)
        : { data: [] },
    ])

    // Build lookup maps
    const profileMap = {}
    for (const p of profileRes.data ?? []) profileMap[p.id] = p

    const quoteMap = {}
    for (const q of quoteRes.data ?? []) quoteMap[q.id] = q

    const bookingMap = {}
    for (const b of bookingRes.data ?? []) bookingMap[b.id] = b

    // Merge into rows
    const enriched = fprs.map((r) => {
      const prof    = profileMap[r.customer_id] ?? {}
      const quote   = quoteMap[r.quote_id]      ?? {}
      const booking = bookingMap[r.booking_id]  ?? {}
      return {
        ...r,
        _fullName: prof.full_name  ?? 'Unknown',
        _email:    prof.email      ?? '—',
        _quoteRef: quote.quote_reference ?? '—',
        _address:  booking.address ?? '',
        _category: booking.service_categories?.name ?? 'Service',
      }
    })

    setRows(enriched)
    setLoading(false)
  }

  useEffect(() => { fetchRows() }, [filter]) // eslint-disable-line

  async function handleRefresh() {
    setRefreshing(true)
    await fetchRows()
    setRefreshing(false)
  }

  function handleStatusChange(id, newStatus) {
    setRows((prev) =>
      prev.map((r) => r.id === id ? { ...r, status: newStatus } : r)
    )
  }

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-primary">Proposals Queue</h1>
            <p className="text-sm text-ink-muted mt-0.5">{rows.length} request{rows.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-ink-muted hover:text-ink-primary transition-colors p-2 rounded-lg hover:bg-surface-overlay"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4 scrollbar-hide">
          {STATUS_OPTS.map((opt) => (
            <button
              key={opt.val}
              onClick={() => setFilter(opt.val)}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors
                ${filter === opt.val
                  ? 'bg-siglat-teal/15 border-siglat-teal text-siglat-teal font-semibold'
                  : 'bg-surface-overlay border-surface-border text-ink-muted hover:text-ink-primary'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-24 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-10 text-center">
            <FileText className="w-8 h-8 text-ink-muted mx-auto mb-2 opacity-30" />
            <p className="text-sm text-ink-muted">No proposal requests found.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {rows.map((row) => (
              <ProposalRow
                key={row.id}
                row={row}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}

      </div>
    </AdminLayout>
  )
}
